import { forwardRef, useCallback, useId, useMemo, useState, Fragment, type CSSProperties, type ReactNode, type TableHTMLAttributes } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { IconButton } from "./IconButton";
import { Checkbox } from "./Checkbox";
import { SearchField } from "./SearchField";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <DataTable> — the
// artifact's own "hardest" pick. Full behavior ported from the vendor's
// compiled bundle (DataTableInner + getNextDirection + SelectionCheckbox),
// not just its type signature: sorting, row selection, row expansion, and
// search all have real (if sparse — 1-3 files each) usage across this
// app's 148 real call sites, confirmed by grep before deciding what to
// port. Nothing here is speculative.
//
// className applies to the wrapper div (.rc-datatable-wrap), not the
// <table> itself — same as the vendor, and worth calling out because it's
// easy to assume otherwise.
export type SortDirection = "asc" | "desc";
export type ColumnAlign = "start" | "center" | "end";

/* Phase 3.1 — row density.
 *
 * `compact` is the DEFAULT, and that is the substantive change: this app is
 * a SOC console, where essentially every table is a triage list an analyst
 * scans hundreds of rows of at a time. The old default was 16px cell padding
 * on 14px text — about 52px a row, which is a reading layout, and it fit
 * roughly nine alerts on a laptop screen. Compact is ~36px (8/12px padding on
 * the 13px `--type-dense` step), which fits about fourteen, and 13px is the
 * size Mercury's and Resend's own dense data tables settle on for the same
 * job (Refero research).
 *
 * `comfortable` is the old default, kept for the genuinely non-list tables —
 * a three-row summary inside a detail card, a settings grid — where dense
 * rows just look cramped. One prop, per table, no className plumbing. */
export type DataTableDensity = "compact" | "comfortable";

export interface DataTableColumn<Row> {
  key: string;
  header: ReactNode;
  cell: (row: Row, rowIndex: number) => ReactNode;
  sortable?: boolean;
  align?: ColumnAlign;
  hidden?: boolean;
  /* What this column sorts BY, when the table is sorting for itself.
     Defaults to `row[key]`, which is right whenever the column key is the
     field name. Supply it when it isn't (`key: "type"` over
     `row.resource_type`), or when the visible cell is a formatted string
     whose alphabetical order is not its real order — a date rendered
     "2 Jan", a duration rendered "1.2s". Ignored when the CALLER owns
     sorting via onSortChange. */
  sortValue?: (row: Row) => string | number | boolean | Date | null | undefined;
}

export type RowKey<Row> = (row: Row, index: number) => string | number;

export interface DataTableSort {
  key: string;
  direction: SortDirection;
}

export interface DataTableProps<Row> extends Omit<TableHTMLAttributes<HTMLTableElement>, "children"> {
  columns: DataTableColumn<Row>[];
  rows: readonly Row[];
  rowKey: RowKey<Row>;
  "aria-label"?: string;
  loading?: boolean;
  skeletonRows?: number;
  empty?: ReactNode;
  sort?: DataTableSort | null;
  onSortChange?: (next: DataTableSort | null) => void;
  selectedKeys?: Set<string | number>;
  onSelectionChange?: (next: Set<string | number>) => void;
  renderExpandedRow?: (row: Row, rowIndex: number) => ReactNode;
  expandedKeys?: Set<string | number>;
  onExpandedChange?: (next: Set<string | number>) => void;
  /* Opens the row. This is a MOUSE AFFORDANCE ONLY — a <tr> cannot be made
     properly focusable or operable without wrecking the table's semantics for
     a screen reader, so a table that sets this must also keep a real focusable
     control in the row (a link on the primary cell) as the keyboard path to
     the same destination. Clicks that land on a button, link or form control
     inside the row are ignored, so per-row actions keep working. */
  onRowClick?: (row: Row, rowIndex: number) => void;
  searchable?: boolean;
  searchQuery?: string;
  onSearchQueryChange?: (next: string) => void;
  searchPlaceholder?: string;
  filterRow?: (row: Row, query: string) => boolean;
  /** Row height. Defaults to `compact` (~36px) — see DataTableDensity. */
  density?: DataTableDensity;
  /* Keeps the column heads visible while the body scrolls under them.
     Only has an effect once the table actually has a vertical scrollport,
     which in practice means `maxHeight` — `.rc-datatable-wrap` is
     `overflow: auto` for horizontal scrolling, and an overflow container is
     the sticky containing block, so a thead inside it can never stick to
     the PAGE no matter what it is told. Left on by default because it costs
     nothing when the table is unbounded. */
  stickyHeader?: boolean;
  /* Bounds the table's own scrollport, which is what makes stickyHeader
     real. Any CSS length: "60vh", 480. */
  maxHeight?: number | string;
}

/* Empty values sort last in BOTH directions, rather than clumping at the top
   when descending. In a SOC table the blank cells are the rows with missing
   telemetry; burying them is right either way, and a reversal that floats
   forty "—" rows to the top makes the sort useless. */
function compareValues(
  a: string | number | boolean | Date | null | undefined,
  b: string | number | boolean | Date | null | undefined
): number {
  const aEmpty = a === null || a === undefined || a === "";
  const bEmpty = b === null || b === undefined || b === "";
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (a instanceof Date || b instanceof Date) {
    return Number(a instanceof Date ? a.getTime() : a) - Number(b instanceof Date ? b.getTime() : b);
  }
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return Number(a) - Number(b);
  // `numeric` so "host-2" precedes "host-10" — the common shape for asset
  // names, rule ids and interface labels in this app.
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function getNextDirection(curr: DataTableSort | null | undefined, key: string): DataTableSort | null {
  if (!curr || curr.key !== key) return { key, direction: "asc" };
  if (curr.direction === "asc") return { key, direction: "desc" };
  return null;
}

function defaultFilter(row: unknown, q: string): boolean {
  const lowerQuery = q.toLowerCase();
  const searchVal = (val: unknown): boolean => {
    if (val === null || val === undefined) return false;
    if (Array.isArray(val)) return val.some(searchVal);
    if (typeof val === "object" && !(val instanceof Date)) return Object.values(val).some(searchVal);
    return String(val).toLowerCase().includes(lowerQuery);
  };
  return searchVal(row);
}

/* Phase 3.1 — the DS Checkbox, not a raw <input type="checkbox">.
 *
 * This was the last place in the app rendering a browser-default checkbox,
 * which meant selection controls looked different in a table than anywhere
 * else, ignored --field-border (the 3:1 control boundary Phase 1.4 added
 * precisely so unchecked boxes are findable), and painted their checked
 * state in the OS accent colour instead of --action-primary.
 *
 * The label is real text, visually hidden rather than omitted: Checkbox
 * requires a label by design, and "Select row" is the accessible name a
 * screen reader needs here anyway. */
function SelectionCheckbox({
  ariaLabel,
  checked,
  indeterminate = false,
  onChange,
}: {
  ariaLabel: string;
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
}) {
  return (
    <Checkbox
      size="sm"
      className="rc-datatable__checkbox"
      label={<span className="sr-only">{ariaLabel}</span>}
      checked={checked}
      indeterminate={indeterminate}
      onChange={onChange}
    />
  );
}

function DataTableInner<Row>(
  {
    columns,
    rows,
    rowKey,
    loading = false,
    skeletonRows = 5,
    empty = "No data",
    sort = null,
    onSortChange,
    selectedKeys,
    onSelectionChange,
    renderExpandedRow,
    expandedKeys,
    onExpandedChange,
    onRowClick,
    searchable = false,
    searchQuery,
    onSearchQueryChange,
    searchPlaceholder,
    filterRow,
    density = "compact",
    stickyHeader = true,
    maxHeight,
    className,
    id: providedId,
    "aria-label": ariaLabel,
    ...rest
  }: DataTableProps<Row>,
  ref: React.ForwardedRef<HTMLTableElement>
) {
  const fallbackId = useId();
  const id = providedId ?? fallbackId;
  const visibleColumns = useMemo(() => columns.filter((c) => !c.hidden), [columns]);
  const selectable = selectedKeys !== undefined && onSelectionChange !== undefined;

  const [localExpandedKeys, setLocalExpandedKeys] = useState<Set<string | number>>(new Set());
  const isExpandedControlled = expandedKeys !== undefined;
  const activeExpandedKeys = isExpandedControlled ? expandedKeys! : localExpandedKeys;
  const setActiveExpandedKeys = isExpandedControlled ? onExpandedChange! : setLocalExpandedKeys;
  const expandable = renderExpandedRow !== undefined;

  /* Sorting has a controlled and an uncontrolled mode, the same split this
     component already makes for search and row expansion.

     Uncontrolled is the new half, and it exists because `sortable: true`
     used to be a LIE without it: the header rendered as a button, took a
     click, and did nothing at all unless the call site also wired up
     `sort` + `onSortChange` and sorted the array itself. Every table that
     declared a sortable column without that plumbing shipped a control
     that looked interactive and was not — the same false-affordance
     problem Phase 3.6 is fixing on cards.
     A caller that passes onSortChange still owns sorting completely
     (server-side paging, custom collation); nothing below runs for them. */
  const [localSort, setLocalSort] = useState<DataTableSort | null>(null);
  const sortControlled = onSortChange !== undefined;
  const activeSort = sortControlled ? (sort ?? null) : localSort;

  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const isSearchControlled = searchQuery !== undefined;
  const activeSearchQuery = isSearchControlled ? searchQuery! : localSearchQuery;

  const filteredRows = useMemo(() => {
    const q = activeSearchQuery.trim();
    if (!q || onSearchQueryChange !== undefined) return rows;
    const filterFn = filterRow ?? defaultFilter;
    return rows.filter((row) => filterFn(row, q));
  }, [rows, activeSearchQuery, onSearchQueryChange, filterRow]);

  // A row key is never allowed to be nullish. When a caller's rowKey reads a
  // field the API didn't send (`row.ID` where the response carries `id`), the
  // raw result is `undefined`, and that fails in three escalating ways:
  // React's "unique key" warning, then broken reconciliation on re-sort, then
  // — worst — collapsed selection, because `selectedKeys.has(undefined)` is
  // true for EVERY such row, so ticking one checkbox visually selects the
  // whole table and a bulk action runs against one meaningless entry.
  // Falling back to the row index keeps all three correct; it costs identity
  // across re-sorts for those rows, which is strictly better than the above.
  const keyOf = useCallback(
    (row: Row, index: number) => rowKey(row, index) ?? index,
    [rowKey]
  );

  const sortedRows = useMemo(() => {
    if (sortControlled || !activeSort) return filteredRows;
    const col = visibleColumns.find((c) => c.key === activeSort.key);
    if (!col) return filteredRows;
    const read = col.sortValue ?? ((row: Row) => (row as Record<string, unknown>)[col.key] as string | number | undefined);
    const dir = activeSort.direction === "asc" ? 1 : -1;
    // Sorts a COPY: `rows` is the caller's array and reordering it in place
    // during a render would mutate their state behind their back.
    return [...filteredRows].sort((a, b) => compareValues(read(a), read(b)) * dir);
  }, [filteredRows, activeSort, sortControlled, visibleColumns]);

  const allKeys = useMemo(
    () => (selectable ? sortedRows.map((r, i) => keyOf(r, i)) : []),
    [sortedRows, keyOf, selectable]
  );
  const allSelected = selectable && allKeys.length > 0 && allKeys.every((k) => selectedKeys!.has(k));
  const someSelected = selectable && !allSelected && allKeys.some((k) => selectedKeys!.has(k));

  const toggleAll = useCallback(() => {
    if (!selectable) return;
    const next = new Set(selectedKeys);
    if (allSelected) allKeys.forEach((k) => next.delete(k));
    else allKeys.forEach((k) => next.add(k));
    onSelectionChange!(next);
  }, [selectable, selectedKeys, allKeys, allSelected, onSelectionChange]);

  const toggleRow = useCallback(
    (key: string | number) => {
      if (!selectable) return;
      const next = new Set(selectedKeys);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      onSelectionChange!(next);
    },
    [selectable, selectedKeys, onSelectionChange]
  );

  const toggleRowExpansion = useCallback(
    (key: string | number) => {
      const next = new Set(activeExpandedKeys);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      setActiveExpandedKeys(next);
    },
    [activeExpandedKeys, setActiveExpandedKeys]
  );

  const handleRowKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTableRowElement>, row: Row, rowIndex: number) => {
      // Only when the row itself has focus: Enter on a button or a checkbox
      // inside the row is that control's own action.
      if (e.target !== e.currentTarget) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onRowClick?.(row, rowIndex);
      }
    },
    [onRowClick]
  );

  const handleRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableRowElement>, key: string | number, row: Row, rowIndex: number) => {
      let target = e.target as HTMLElement | null;
      while (target && target !== e.currentTarget) {
        const tagName = target.tagName.toLowerCase();
        if (
          tagName === "button" ||
          tagName === "input" ||
          tagName === "select" ||
          tagName === "textarea" ||
          tagName === "a" ||
          target.classList.contains("rc-datatable__selection-cell") ||
          target.getAttribute("role") === "button"
        ) {
          return;
        }
        target = target.parentElement;
      }
      // A text selection that happens to end inside the row is not a click on
      // it — without this, dragging to copy an alert title navigates away and
      // loses the selection.
      if (window.getSelection()?.toString()) return;
      if (onRowClick) onRowClick(row, rowIndex);
      else toggleRowExpansion(key);
    },
    [toggleRowExpansion, onRowClick]
  );

  function onHeaderClick(col: DataTableColumn<Row>) {
    if (!col.sortable) return;
    const next = getNextDirection(activeSort, col.key);
    if (sortControlled) onSortChange!(next);
    else setLocalSort(next);
  }

  const columnCount = visibleColumns.length + (selectable ? 1 : 0) + (expandable ? 1 : 0);
  const hasRows = sortedRows.length > 0;

  const wrapStyle: CSSProperties | undefined = maxHeight === undefined
    ? undefined
    : { maxHeight: typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight };

  return (
    <div
      className={cn("rc-datatable-wrap", className)}
      data-density={density}
      data-sticky-header={stickyHeader || undefined}
      style={wrapStyle}
    >
      {searchable || isSearchControlled ? (
        <div className="rc-datatable__search-bar">
          {/* d2d-XDR eval F20: the DS SearchField rather than a raw input —
              a real (visually hidden) label, a clear button, and Escape to
              clear that still hands Escape to a surrounding dialog when the
              field is empty. */}
          <SearchField
            value={activeSearchQuery}
            onChange={(val) => {
              if (onSearchQueryChange) onSearchQueryChange(val);
              else setLocalSearchQuery(val);
            }}
            label="Search table"
            placeholder={searchPlaceholder ?? "Search..."}
            size="sm"
            className="rc-datatable__search-container"
          />
        </div>
      ) : null}
      {/* aria-busy matters here because every skeleton row below is aria-hidden:
          without it assistive tech is handed a table with headers and no rows
          at all, which is indistinguishable from "no results" rather than
          "still loading". */}
      <table
        ref={ref}
        id={id}
        aria-label={ariaLabel}
        aria-busy={loading || undefined}
        className="rc-datatable"
        {...rest}
      >
        <thead>
          <tr>
            {selectable ? (
              <th scope="col" className="rc-datatable__selection-cell">
                <SelectionCheckbox
                  ariaLabel={allSelected ? "Deselect all rows" : "Select all rows"}
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                />
              </th>
            ) : null}
            {expandable ? <th scope="col" className="rc-datatable__expand-header-cell" /> : null}
            {visibleColumns.map((col) => {
              const isSorted = activeSort?.key === col.key;
              const ariaSort = isSorted ? (activeSort!.direction === "asc" ? "ascending" : "descending") : col.sortable ? "none" : undefined;
              return (
                <th key={col.key} scope="col" data-align={col.align ?? "start"} aria-sort={ariaSort}>
                  {col.sortable ? (
                    <button type="button" className="rc-datatable__sort-button" onClick={() => onHeaderClick(col)}>
                      {col.header}
                      <span className="rc-datatable__sort-indicator" aria-hidden>
                        {isSorted && activeSort!.direction === "desc" ? <ChevronDown /> : <ChevronUp />}
                      </span>
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading
            ? Array.from({ length: skeletonRows }, (_, i) => (
                <tr key={`__skeleton-${i}`} className="rc-datatable__skeleton-row" aria-hidden>
                  {selectable ? (
                    <td className="rc-datatable__selection-cell">
                      <div />
                    </td>
                  ) : null}
                  {expandable ? (
                    <td className="rc-datatable__expand-cell">
                      <div />
                    </td>
                  ) : null}
                  {visibleColumns.map((c) => (
                    <td key={c.key}>
                      <div />
                    </td>
                  ))}
                </tr>
              ))
            : hasRows
              ? sortedRows.map((row, rowIndex) => {
                  const key = keyOf(row, rowIndex);
                  const selected = selectable && selectedKeys!.has(key);
                  const expanded = expandable && activeExpandedKeys.has(key);
                  return (
                    <Fragment key={key}>
                      <tr
                        // d2d-DetectHub eval F13: aria-selected is not valid on a plain
                        // table row (only in a grid), so selection is data-selected; the
                        // checkbox in the row is what says "selected" to assistive tech.
                        data-selected={selectable && selected ? "true" : undefined}
                        data-expanded={expanded || undefined}
                        data-expandable={expandable || undefined}
                        onClick={expandable || onRowClick ? (e) => handleRowClick(e, key, row, rowIndex) : undefined}
                        // A clickable row is a tab stop and opens on Enter / Space, so the
                        // detail it leads to is reachable without a mouse.
                        tabIndex={onRowClick ? 0 : undefined}
                        onKeyDown={onRowClick ? (e) => handleRowKeyDown(e, row, rowIndex) : undefined}
                        data-clickable={onRowClick ? "true" : undefined}
                      >
                        {selectable ? (
                          <td className="rc-datatable__selection-cell">
                            <SelectionCheckbox
                              ariaLabel={selected ? "Deselect row" : "Select row"}
                              checked={Boolean(selected)}
                              onChange={() => toggleRow(key)}
                            />
                          </td>
                        ) : null}
                        {expandable ? (
                          <td className="rc-datatable__expand-cell">
                            <IconButton
                              size="sm"
                              aria-label={expanded ? "Collapse row" : "Expand row"}
                              icon={expanded ? <ChevronUp /> : <ChevronDown />}
                              onClick={() => toggleRowExpansion(key)}
                            />
                          </td>
                        ) : null}
                        {visibleColumns.map((col) => (
                          <td key={col.key} data-align={col.align ?? "start"}>
                            {col.cell(row, rowIndex)}
                          </td>
                        ))}
                      </tr>
                      {expanded ? (
                        <tr className="rc-datatable__expanded-row">
                          <td colSpan={columnCount} className="rc-datatable__expanded-cell">
                            {renderExpandedRow!(row, rowIndex)}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })
              : (
                <tr>
                  <td colSpan={columnCount} className="rc-datatable__state">
                    {empty}
                  </td>
                </tr>
              )}
        </tbody>
      </table>
    </div>
  );
}

export const DataTable = forwardRef(DataTableInner) as <Row>(
  props: DataTableProps<Row> & { ref?: React.ForwardedRef<HTMLTableElement> }
) => ReturnType<typeof DataTableInner>;
